import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

const pct = (value) => `${Math.round(value * 100)}%`;

// Conclusão baixa com bastante gente abrindo é o sinal de lição a rever
const completionColor = (opened, rate) => {
  if (opened < 3) return 'text.primary';
  if (rate < 0.4) return '#C62828';
  if (rate < 0.7) return '#E65100';
  return '#2E7D32';
};

const COLUMNS = [
  { key: 'lesson', label: 'Lição' },
  { key: 'opened', label: 'Abriram', align: 'right' },
  { key: 'completed', label: 'Assistiram', align: 'right' },
  { key: 'stop', label: 'Param em média', align: 'right' },
  { key: 'help', label: 'Pela ajuda da tela', align: 'right' },
  { key: 'helpful', label: 'Ajudou nesta versão (sim / não)', align: 'right' },
];

function LessonStatsRow({ lesson, stats }) {
  const opened = stats?.users_opened || 0;
  const completed = stats?.users_completed || 0;
  const rate = opened ? completed / opened : 0;
  // Versão nova: o "ajudou?" mostrado é só o da versão atual; assistiram soma todas
  const version = lesson.version || 1;
  const current = (stats?.versions || []).find((entry) => entry.version === version)
    || { users_completed: 0, helpful_yes: 0, helpful_no: 0 };

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{lesson.number}. {lesson.title}</Typography>
        {version > 1 && <Typography variant="caption" color="text.secondary">Versão {version}</Typography>}
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{opened}</Typography>
        {stats?.companies ? <Typography variant="caption" color="text.secondary">{stats.companies} empresa(s)</Typography> : null}
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" sx={{ fontWeight: 600, color: completionColor(opened, rate) }}>
          {opened ? `${completed} (${pct(rate)})` : '—'}
        </Typography>
        {version > 1 && opened > 0 && (
          <Typography variant="caption" color="text.secondary">{current.users_completed} na versão atual</Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{stats?.avg_stop_ratio == null ? '—' : pct(stats.avg_stop_ratio)}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{stats?.users_from_help ? `${stats.users_from_help} pessoa(s)` : '—'}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{stats ? `${current.helpful_yes} / ${current.helpful_no}` : '—'}</Typography>
      </TableCell>
    </TableRow>
  );
}

/**
 * TutorialStatsTable - Lições de um capítulo com os números somados de todas as empresas.
 * "Param em média" considera só quem abriu e não assistiu até o fim. Lição com versão nova:
 * "Ajudou" conta só a versão atual e "Assistiram" mostra quantos já viram a versão atual.
 * @param {Object} props
 * @param {Object} props.chapter - { number, title, lessons }
 * @param {Object} props.statsById - números por id da lição (stats.php)
 */
function TutorialStatsTable({ chapter, statsById }) {
  const lessons = chapter.lessons || [];

  return (
    <Paper
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #E8ECF0',
        overflow: 'hidden',
        mb: 3,
      }}
    >
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #E8ECF0', display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{chapter.number}. {chapter.title}</Typography>
        <Typography variant="body2" color="text.secondary">({lessons.length} lições)</Typography>
      </Box>

      <TableContainer sx={{ boxShadow: 'none', borderRadius: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8F9FC' }}>
              {COLUMNS.map((col) => (
                <TableCell key={col.key} align={col.align || 'left'} sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem' }}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {lessons.map((lesson) => (
              <LessonStatsRow key={lesson.id} lesson={lesson} stats={statsById[lesson.id]} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default TutorialStatsTable;
